import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { RADIUS, TYPE, useTheme } from '../../theme';
import { useIsMobile, useOverlay } from './shared';
import { IconButton } from './Button';

const Z = 10000;

function Scrim({ onClose, children, align }: { onClose: () => void; children: React.ReactNode; align: React.CSSProperties }) {
  const T = useTheme();
  return createPortal(
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: Z, background: T.scrim, display: 'flex', ...align }}>
      {children}
    </div>,
    document.body,
  );
}

function Header({ title, onClose, id }: { title?: React.ReactNode; onClose: () => void; id: string }) {
  const T = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px 0 20px', minHeight: 52 }}>
      <h2 id={id} style={{ ...TYPE.title, color: T.text, margin: 0, flex: 1, minWidth: 0 }}>{title}</h2>
      <IconButton label="Stäng" onClick={onClose}><X size={20} strokeWidth={2} /></IconButton>
    </div>
  );
}

type OverlayProps = {
  open: boolean; onClose: () => void; title?: React.ReactNode;
  children: React.ReactNode;
  /** Fast fotrad med knappar (t.ex. Avbryt + primär). */
  actions?: React.ReactNode;
};

function Footer({ actions }: { actions?: React.ReactNode }) {
  const T = useTheme();
  if (!actions) return null;
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', padding: '12px 20px calc(16px + env(safe-area-inset-bottom, 0px))', borderTop: `1px solid ${T.border}` }}>
      {actions}
    </div>
  );
}

/** Bottenark: radie 22, handtag, scrollbart innehåll. Escape och klick på bakgrunden stänger. */
export function Sheet({ open, onClose, title, children, actions, maxWidth = 560 }: OverlayProps & { maxWidth?: number }) {
  const T = useTheme();
  const ref = React.useRef<HTMLDivElement>(null);
  const id = React.useId();
  useOverlay(open, onClose, ref);
  if (!open) return null;
  return (
    <Scrim onClose={onClose} align={{ alignItems: 'flex-end', justifyContent: 'center' }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={title ? id : undefined} tabIndex={-1}
        style={{
          width: '100%', maxWidth, maxHeight: '90dvh', display: 'flex', flexDirection: 'column', outline: 'none',
          background: T.surface, color: T.text, borderRadius: `${RADIUS.xl}px ${RADIUS.xl}px 0 0`, boxShadow: T.shadow.sheet,
        }}>
        <div aria-hidden style={{ width: 36, height: 5, borderRadius: 3, background: T.borderHover, margin: '8px auto 0', flexShrink: 0 }} />
        {title !== undefined && <Header title={title} onClose={onClose} id={id} />}
        <div style={{ overflowY: 'auto', padding: '12px 20px 20px', flex: 1 }}>{children}</div>
        <Footer actions={actions} />
      </div>
    </Scrim>
  );
}

/** Sidopanel från vänster eller höger, full höjd. */
export function Drawer({ open, onClose, title, children, actions, side = 'left', width = 340 }: OverlayProps & { side?: 'left' | 'right'; width?: number }) {
  const T = useTheme();
  const ref = React.useRef<HTMLDivElement>(null);
  const id = React.useId();
  useOverlay(open, onClose, ref);
  if (!open) return null;
  return (
    <Scrim onClose={onClose} align={{ justifyContent: side === 'left' ? 'flex-start' : 'flex-end' }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={title ? id : undefined} tabIndex={-1}
        style={{
          width: `min(86vw, ${width}px)`, height: '100%', display: 'flex', flexDirection: 'column', outline: 'none',
          background: T.surface, color: T.text, boxShadow: T.shadow.raised,
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}>
        {title !== undefined && <Header title={title} onClose={onClose} id={id} />}
        <div style={{ overflowY: 'auto', padding: '8px 12px 20px', flex: 1 }}>{children}</div>
        <Footer actions={actions} />
      </div>
    </Scrim>
  );
}

/** Dialog: centrerad på desktop (radie 22), bottenark på mobil. */
export function Dialog({ open, onClose, title, children, actions, width = 480 }: OverlayProps & { width?: number }) {
  const T = useTheme();
  const mob = useIsMobile();
  const ref = React.useRef<HTMLDivElement>(null);
  const id = React.useId();
  useOverlay(open && !mob, onClose, ref);
  if (mob) return <Sheet open={open} onClose={onClose} title={title} actions={actions}>{children}</Sheet>;
  if (!open) return null;
  return (
    <Scrim onClose={onClose} align={{ alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={title ? id : undefined} tabIndex={-1}
        style={{
          width: '100%', maxWidth: width, maxHeight: '90dvh', display: 'flex', flexDirection: 'column', outline: 'none',
          background: T.surface, color: T.text, borderRadius: RADIUS.xl, boxShadow: T.shadow.raised,
        }}>
        {title !== undefined && <Header title={title} onClose={onClose} id={id} />}
        <div style={{ overflowY: 'auto', padding: '12px 20px 20px', flex: 1 }}>{children}</div>
        <Footer actions={actions} />
      </div>
    </Scrim>
  );
}
